import { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ContentCopy, DisabledByDefaultRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import COLORS from '@/resources/style/Themes';
import { DASHBOARD_COLORS } from './styles';

const COPY_FEEDBACK_DURATION_MS = 2000;
const CLOSE_BUTTON_RIGHT_PADDING = '48px';

type DeviceOnlyDashboardHeaderProps = {
  onClose: () => void;
  lastUserDisplayName: string | null;
  lastUserPhoneNumber: string | null;
};

/**
 * Minimal header for the device-only UserDashboard path (no resolvable user).
 * Parallel to UserDashboardHeader — keeps UserDashboard as the open-gate/orchestrator.
 * Shares UserDashboardHeader's color/shape treatment (DASHBOARD_COLORS,
 * sticky top-radius container, absolute-positioned close button) so the two
 * headers read as the same family of component instead of two unrelated modals.
 */
const DeviceOnlyDashboardHeader = ({
  onClose,
  lastUserDisplayName,
  lastUserPhoneNumber,
}: DeviceOnlyDashboardHeaderProps) => {
  const { t } = useTranslation();
  const [isPhoneCopied, setIsPhoneCopied] = useState(false);

  const hasDisplayName = Boolean(lastUserDisplayName);
  const hasPhone = Boolean(lastUserPhoneNumber?.trim());

  const handlePhoneCopyClick = async () => {
    if (!lastUserPhoneNumber) return;
    try {
      await navigator.clipboard.writeText(lastUserPhoneNumber);
      setIsPhoneCopied(true);
      setTimeout(() => {
        setIsPhoneCopied(false);
      }, COPY_FEEDBACK_DURATION_MS);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: DASHBOARD_COLORS.background,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        top: 0,
        position: 'sticky',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        zIndex: 1400,
        padding: '16px 16px 16px 16px',
        paddingRight: CLOSE_BUTTON_RIGHT_PADDING,
      }}
    >
      {hasDisplayName ? (
        <Box sx={{ width: '100%' }}>
          <Typography
            variant="h5"
            sx={{
              fontSize: '1.4rem',
              fontWeight: 'bold',
              mb: 1,
              color: DASHBOARD_COLORS.header,
            }}
          >
            {lastUserDisplayName}
          </Typography>
          {hasPhone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="body1"
                sx={{ fontSize: '.9rem', color: COLORS.GREY }}
              >
                {lastUserPhoneNumber}
              </Typography>
              <ContentCopy
                onClick={handlePhoneCopyClick}
                sx={{
                  fontSize: '.9rem',
                  color: isPhoneCopied ? COLORS.SAFEGUARD_ORANGE : COLORS.GREY,
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: COLORS.SAFEGUARD_ORANGE },
                }}
              />
            </Box>
          )}
        </Box>
      ) : (
        <Typography
          variant="h5"
          sx={{
            fontSize: '1.4rem',
            fontWeight: 'bold',
            mb: 1,
            color: DASHBOARD_COLORS.header,
          }}
        >
          {t(StringKeys.DEVICE_DETAILS)}
        </Typography>
      )}

      {/* Close Button */}
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{
          position: 'absolute',
          top: 5,
          right: 5,
          color: DASHBOARD_COLORS.header,
          zIndex: 1500,
        }}
      >
        <DisabledByDefaultRounded />
      </IconButton>
    </Box>
  );
};

export default DeviceOnlyDashboardHeader;
